import { HttpContextToken } from '@angular/common/http';
import { Validators } from '@angular/forms';
import { HttpLoggingOptions } from './models/http-logging-options.model';

export const MIN_DATE_DOTNET_STRING = '0001-01-01T00:00:00';

export enum AuthenticationState {
  unauthorised = 'unauthorised',
  selectAccountCode = 'accountcode',
  selectAccount = 'selectAccount',
  credentials = 'credentials',
  authCodeRequested = 'authcodeRequested',
  accountVerified = 'accountVerified',
  noAccounts = 'noAccounts',
  mfaRequired = 'mfa',
}

export const SnackbarStatus = {
  Information: { bg_color: '#3277bc', icon_color: '#3277bc', icon: 'info' },
  Attention: { bg_color: '#3277bc', icon_color: '#ff4d00', icon: 'warning' },
  Warning: { bg_color: '#ff4d00', icon_color: '#ff4d00', icon: 'warning' },
};

export const strictEmailValidator = Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/);

export enum ListType {
  key = 'key',
  custom = 'custom',
  radioService = 'radio',
  businessRadio = 'radioBR',
  amateurRadio = 'radioTR',
  pmse = 'radioTB',
}
export const MarketDataLCAutos: Record<number, number> = {
  1: 126817,
  2: 135823,
  3: 136313,
  4: 136314
}

export abstract class Constants {
  public static readonly NumberMinValueDotNet: number = Math.pow(2, 31) * -1; //equivalent to int.minvalue in c# (-2147483648)

  public static get irelandCountryString() {
    return 'Ireland';
  }

  public static get irelandCountryCode() {
    return '372';
  }

  public static get lookupUrls() {
    return {
      typeKey: '/Lookups/types/:type',
      countries: '/Lookups/countries',
      counties: '/Lookups/counties',
      regions: '/Lookups/regions',
      venues: '/Lookups/venues',
      aircraftTypes: '/Lookups/radio/ar/aircraft-types',
      countyWithVenues: '/Lookups/county-with-venues',
      radioPurposes: '/Lookups/radio/purposes',
      equipmentPrices: '/Lookups/radio/:licence-type/equipment-prices',
      baseStationTypes: '/Lookups/radio/br/base-station-types',
      automaticTypes: '/Lookups/radio/er/automatic-types',
      equipmentFixedPrices: '/Lookups/radio/pmse/equipment-fixed-prices',
      equipmentTypes: '/Lookups/radio/pmse/equipment-types',
      specialEvents: '/Lookups/radio/pmse/special-events',
      prsOrganisationTypes: '/Lookups/prs/organisation-type',
      salutations: '/Lookups/salutations',
      prsClass: '/Lookups/prs/class',
      prsSubCategory: '/Lookups/prs/sub-category',
      equipmentBrFixedPrices: '/Lookups/radio/br/equipment-prices',
      frequencyBands: '/Lookups/radio/sa',
    };
  }

  public static get claimTypes() {
    return {
      multifactor: 'cr:multifactor',
      erauReference: 'cr:erauReference',
      isSuperUser: 'cr:IsSuperUser',
      providerType: 'cr:ProviderTypeString',
      accountName: 'cr:accountName',
      userEmail: 'cr:userEmail',
      associatedAccount: 'cr:AssociatedAccount',
      firstName: 'cr:firstName',
      lastName: 'cr:lastName',
    };
  }

  public static get permissionGroups() {
    return {
      hasNoOnlineAccess: -1,
      hasOnlineAccess: 0,
      administration: {
        canModifyOnlineAccess: 1,
        canModifyContacts: 2,
        canModifyAccountDetails: 4,
      },
      eLicensing: {},
      licensing: {},
      finance: {},
      DataPortal: {
        submissions: {
          canSubmitFixedBroadband: 8,
          canSubmitLeasedLines: 16,
          canSubmitNetworkMaps: 32,
          canSubmitSpacialData: 64,
          canSubmitRevenue: 128,
          canSubmitOffers: 256,
          canSubmitInvestment: 512,
          canSubmitMobile: 1024,
          canSubmitComplaints: 2048,
          canSubmitPIA: 4096,
          canSubmitScamMetrics: 8192,
        },
      },
    };
  }
}

export abstract class HttpContextTokens {
  public static options: HttpContextToken<HttpLoggingOptions> = new HttpContextToken<HttpLoggingOptions>(() => new HttpLoggingOptions());
}

export enum UserType {
  standard = 0,
  superUser = 1,
}

export enum AccessPermission {
  canLogin = 0,
  canModifyOnlineAccess = 1,
  canModifyAccountDetails = 2,
  canApplyForLicences = 3,
  canCancelLicences = 4,
}

export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARNING = 3,
  ERROR = 4,
  CRITICAL = 5,
  NEVER = 99,
}

export enum PopupStatus {
  Information = 1,
  Warning = 2,
  Error = 3,
}

export enum ProviderType {
  PRS = 1,
  RADIO = 2,
  NUMBERING = 3,
  MARKETDATA = 4,
  NETWORKOPERATIONS = 5,
  SERVICEREGISTER = 6,
  BROADCASTING = 7,
  SENDERID = 8,
}

export enum Days {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export enum NewAccountType {
  Radio = 'RADIO',
  Prs = 'PRS',
}

export enum NewAccountEntityType {
  Company = 'COMPANY',
  Person = 'PERSON',
}

export enum ContactAttribute {
  Regulatory = 'Regulatory',
  SQU = 'SQU',
  Director = 'Director',
  Numbering = 'Numbering',
  SMS = 'SMS',
  ISPC = 'ISPC',
  MNC = 'MNC',
  NSPC = 'NSPC',
  ClubAccount = 'Club Account',
  Primary = 'Primary',
}

export enum PrsOrganisationType {
  LimitedCompany = '1',
  Partnership = '2',
  SoleTrader = '3',
  NotForProfit = '4',
  SemiState = '5',
  StateOwned = '6',
  Charity = '7',
}

export enum LicenceStatus {
  Cancelled = 'C',
  Licenced = 'L',
  Modification = 'M',
  OriginalLicence = 'O',
  TemporaryLicence = 'T',
  Withdraw = 'W',
}

export enum DownloadMediaFileType {
  Excel = 'excel',
  PDF = 'pdf',
  XML = 'xml',
}

export enum WizardButtonActions {
  Next = 'next',
  Previous = 'previous',
  Submit = 'submit',
}

export enum TopUpType {
  Amount = 'amount',
  Quotation = 'quotation',
}

export const comToolTips: Record<string, string> = {
  Coordinates:
    'Input must be Degrees/Minutes/Seconds - not Decimal Degrees. Degree value must be two digits (Minus is not accepted) e.g. "-7W" should be input as "07"',
};

export enum TypeFile {
  Licences = 'Licences',
}

export enum ApplicationId {
  eLicensing = 'EL',
  DataPortal = 'DP',
  SenderId = 'SI',
}

export enum ClientType {
  eLicensing = 'eLicensing',
  DataPortal = 'DataPortal',
  SenderId = 'SenderId',
}
